import { prepareFeaturing } from '~/utils/featuring';
import { prepareCategoriesForState } from '~/utils/category';
import { prepareCurrenciesForState } from '~/utils/currencies';
import { arrayToObjectByKey, isEmptyObject } from '~/utils/utils';
import { getPrimaryItem, isParagonBundle, isRandomBundle } from '~/utils/bundles';
import Account from '~/account/Account';
import { CATEGORIES, BUNDLE_GRID_SIZE } from '~/const';
import { clearString, normalizeString } from '~/utils/normalization';
import { logInfo } from '~/utils/logging';
import { BundlePurchaseTypes, BUNDLE_TYPES } from '~/types/bundle';
import { WarmingUpCollections } from '@wg/wows-entities/const';
import { ManagerData } from '~/core/ManagerData';

export type GlobalArmoryContentForState = {
    categories: ICategories;
    currencies: Currency[];
    coupons: ICoupon[];
    featuring: IFeature[];
    notifications: INotification[];
};

export const getArmoryContentFromSettings = ((): ((bundles: Nullable<IBundleList>, vortexCurrencies: Nullable<IVortexCurrency[]>) => Partial<GlobalArmoryContentForState>) => {
    const result: Partial<GlobalArmoryContentForState> = {};

    return function (bundles, vortexCurrencies): Partial<GlobalArmoryContentForState> {
        if (!isEmptyObject(result)) {
            return result;
        }

        const { content } = window.metashop.state;

        Object.keys(content).forEach((key: keyof GlobalArmoryContentForState) => {
            let data;

            switch (key) {
                case 'featuring':
                    if (!result.categories) {
                        result.categories = prepareCategoriesForState(bundles);
                    }
                    data = prepareFeaturing(bundles, result.categories);
                    break;

                case 'categories':
                    if (!result.categories) {
                        data = prepareCategoriesForState(bundles);
                    }
                    break;

                case 'currencies':
                    let currencies = vortexCurrencies;
                    if (!currencies) {
                        currencies = Object.values(ManagerData.getDataBySection(WarmingUpCollections.currencies));
                    }
                    if (currencies) {
                        data = prepareCurrenciesForState(currencies);
                    }
                    break;

                default:
                    data = content[key] as any;
            }

            result[key] = data;
        });

        return result;
    };
})();

const prepareAutoDescriptionSettings = (bundle: IRandomBundleChild | IBundle) => {
    if (bundle?.autoDescriptionSettings?.lootboxSettings) {
        bundle.autoDescriptionSettings.lootboxSettings = arrayToObjectByKey(bundle.autoDescriptionSettings.lootboxSettings as ILootboxAutoDescriptionSettings[], 'id');
    }
};

export const prepareArmoryBundles = (bundleList: IBundleList, categories: ICategories) => {
    const result: IBundleList = {};

    const bundles: IBundle[] = Object.values(bundleList);
    const hiddenBundles: number[] = [];
    const disabledBundles: number[] = [];

    bundles.forEach((bundle) => {
        if (disabledBundles.includes(bundle.id)) {
            return;
        }

        const _isParagon = isParagonBundle(bundle);
        if (_isParagon && !Account.isParagonsEnabled()) {
            return;
        }

        if (bundle.productCode) {
            bundle.isLoading = true;
            bundle.price = null;
            bundle.entitlements = [];
            bundle.purchaseType = BundlePurchaseTypes.REAL_CURRENCY;
        }

        bundle.isRandom = isRandomBundle(bundle);

        if (bundle.isRandom && !bundle.randomBundleChildren?.length) {
            return;
        }

        const category: string[] = Object.values(categories)
            .filter((category: ICategory) => category.bundles?.includes(bundle.id))
            .map((category) => category.name);

        bundle.categories = category;

        bundle.normalizedTitle = clearString(normalizeString(bundle.title));
        bundle.primaryItem = getPrimaryItem(bundle.entitlements) as BundlePrimaryType;

        if (bundle.price === 0 && bundle.additionalPrice === 0) {
            bundle.additionalPrice = null;
            bundle.additionalCurrency = null;
        }

        if (!bundle.sizeInGrid) {
            if (bundle.categories.includes(CATEGORIES.SHIPS)) {
                bundle.sizeInGrid = BUNDLE_GRID_SIZE.ONE_THIRD as SIZE_IN_GRID;
            } else {
                bundle.sizeInGrid = BUNDLE_GRID_SIZE.ONE_FOUR as SIZE_IN_GRID;
            }
        }

        if (!isEmptyObject(bundle.autoDescriptionSettings)) {
            prepareAutoDescriptionSettings(bundle);
        }

        bundle.randomBundleChildren?.forEach((randomBundleChild) => {
            prepareAutoDescriptionSettings(randomBundleChild);
        });

        if (bundle.serialSequence?.length) {
            if (bundle.type && bundle.type !== BUNDLE_TYPES.serial) {
                bundle.subType = bundle.type;
            }
            bundle.type = BUNDLE_TYPES.serial;
            hiddenBundles.push(...bundle.serialSequence);
            const isCrookedSerialSequence = bundle.serialSequence.some((serialBundleId) => {
                if (isParagonBundle(bundleList[serialBundleId]) && !Account.isParagonsEnabled()) {
                    return true;
                }
                return !bundleList[serialBundleId];
            });
            if (isCrookedSerialSequence) {
                logInfo(`Serial bundle ${bundle.id} was hidden because had a crook sequence`);
                return;
            }
        }

        result[bundle.id] = bundle;
    });

    hiddenBundles.forEach((bundleId: number) => {
        if (result[bundleId]) {
            result[bundleId].isHidden = true;
        }
    });

    return result;
};
